import { useQueryClient } from '@tanstack/react-query';
import Router from 'next/router';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { Boards, type GetBoardResponse } from '@air/api';
import { type Board } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { getPrivateBoardKey } from '~/components/v2/hooks/queries/usePrivateBoard';
import { getCanvasQueryParams } from '~/constants/canvasQueryParams';
import { createBoardRoute } from '~/constants/routes';
import { setCentralizedBoardAction, setCentralizedBoardIdAndTitleAction } from '~/store/centralizedBoard/actions';
import { type LocationBoard } from '~/store/router/types';

export function useGoToBoardPage() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const goToBoardPage = useCallback(
    async ({
      method,
      board,
    }: {
      board: LocationBoard | Board;
      trackLocation?: string;
      method?: 'push' | 'replace';
    }) => {
      Router.ready(async () => {
        const boardRoute = createBoardRoute(board.id, board.title);

        // Prevents navigation if the user clicks on the same board.
        const currentBoardId = Array.isArray(Router.query.boardId) ? Router.query.boardId[0] : Router.query.boardId;

        if (currentBoardId === board.id) {
          return;
        }

        const cachedBoard = queryClient.getQueryData<GetBoardResponse>(getPrivateBoardKey({ boardId: board.id }));

        if (cachedBoard) {
          dispatch(setCentralizedBoardAction({ board: cachedBoard }));
        } else {
          dispatch(setCentralizedBoardIdAndTitleAction({ board }));
        }

        const routerFn = method === 'replace' ? Router.replace : Router.push;
        await routerFn({ pathname: boardRoute, query: getCanvasQueryParams() });

        try {
          await Boards.viewBoard({ boardId: board.id });
        } catch (error) {
          reportErrorToBugsnag({
            error,
            context: 'Failed to trigger a view for a board',
            metadata: { data: { boardId: board.id } },
          });
        }
      });
    },
    [dispatch, queryClient],
  );

  return {
    goToBoardPage,
  };
}
